///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The storm column: a block of atmosphere lifted off the globe above a
/// focused tropical cyclone. Its lid is the storm's cloud-top relief from
/// the storm-centered IR frames, its floor the modeled surface wind, and
/// its two far walls carry the along- and across-motion sections (cloud
/// tops through the center, the wind profile as an inset) with kilometer
/// and pressure-level rules. Forecast intensity stands along the track as
/// translucent columns.
///
/// Block frame: x along the storm's motion, y up, z across, km. The node
/// origin sits at the center of the box at mid height (what the camera
/// orbits); the `frame` child shifts so that y = 0 is the sea surface.
///

import { type FarWalls } from "@/earthbrowser/dataset-distribution/earthquakes/cutout/CutoutWallsNode"
import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { Cube } from "@/earthbrowser/entities/geometries/Cube"
import { type StormCutoutBuoy } from "@/redux/dialogSlice"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { TextNode } from "@/earthbrowser/entities/TextNode/TextNode"
import { type Font } from "@/earthbrowser/libs/opentype"
import { FontCache } from "@/earthbrowser/utils/FontCache"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { animation, easing } from "@stovokor/math/animation"
import { rgba } from "@stovokor/math/color"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { cross, dot, normal, vec3 } from "@stovokor/math/vector"
import { type StormProperties, type StormTrackPoint, type WindProbability } from "../HurricanesTable"
import { polygonsFor, probabilityColor, type WindProbabilityThreshold } from "../windProbability"
import { type StormCloudField } from "./StormCloudField"
import { StormCloudLidNode } from "./StormCloudLidNode"
import { StormColumnLightningNode } from "./StormColumnLightningNode"
import { type StormColumnFlash } from "./stormColumnLightning"
import { StormColumnWallsNode } from "./StormColumnWallsNode"
import { StormSectionNode } from "./StormSectionNode"
import { type StormSstField } from "./StormSstField"
import { StormSstSlabNode } from "./StormSstSlabNode"
import { StormWindFloorNode } from "./StormWindFloorNode"
import { blockBearingRad, blockToLatLon, boxHeightKm, clamp, cPressureLevels, insideColumn, latLonToBlock, type StormColumnLayout } from "./stormColumnLayout"
import { cloudTopKm, hollandWindKt, type WindModel } from "./stormColumnStats"

const cLabelFont = "/fonts/Inter-Regular.otf"
/** Default view: elevation above the block and azimuth off the across-motion axis */
const cDefaultTiltFromNadir = 55 * trig.rad
const cDefaultAzimuth = 35 * trig.rad
/** Section presets: nearly edge-on to a wall, a little above the sea surface */
const cSectionTiltFromNadir = 76 * trig.rad
/** Overhead preset: almost straight down, enough tilt to keep the lid's relief */
const cOverheadTiltFromNadir = 8 * trig.rad

export type StormColumnViewPreset = "oblique" | "along" | "across" | "overhead"
/** Wind inset on the walls: this wind reaches this fraction of the box height */
const cWindInsetTopKt = 160
const cWindInsetFraction = 0.28
/** Forecast wind that fills the full box height */
const cColumnFullKt = 150
/** Samples along a wall section */
const cSectionSamples = 160

const cEdgeColor = rgba(0.80, 0.86, 0.94, 0.30)
const cPlumbColor = rgba(1, 1, 1, 0.55)
const cPlumbGroundColor = rgba(1, 1, 1, 0.28)
const cLabelColor = rgba(0.86, 0.90, 0.96, 1)
const cLabelDimColor = rgba(0.62, 0.68, 0.76, 1)
const cPressureLabelColor = rgba(0.58, 0.77, 0.99, 1)
const cCloudSectionColor = rgba(0.56, 0.82, 1.0, 0.16)
const cWindSectionColor = rgba(1.0, 0.62, 0.15, 0.26)
const cTrackObservedColor = rgba(1, 1, 1, 0.30)
const cTrackForecastColor = rgba(1, 1, 1, 0.55)
const cBuoyColor = rgba(0.98, 0.75, 0.14, 0.95)
const cShearColor = rgba(1.0, 0.88, 0.55, 0.92)
const cBuoyLabelColor = rgba(0.99, 0.90, 0.60, 1)
/** Buoy pin height as a fraction of the box height */
const cBuoyPinFraction = 0.12

interface BuoyPin {
	post: PolylineNode
	bead: SimpleNode
	label?: TextNode
	x: number
	z: number
	height: number
	text: string
}

/** "41010 · 997 hPa · gusts 52 kt · 8.1 m" with whatever the station reports */
function buoyLabelText(buoy: StormCutoutBuoy): string {
	const parts = [buoy.stationId]
	if (buoy.pressureHpa !== null) parts.push(`${Math.round(buoy.pressureHpa)} hPa`)
	if (buoy.windGustMps !== null) parts.push(`gusts ${Math.round(buoy.windGustMps / 0.514444)} kt`)
	else if (buoy.windSpeedMps !== null) parts.push(`${Math.round(buoy.windSpeedMps / 0.514444)} kt`)
	if (buoy.waveHeightM !== null) parts.push(`${buoy.waveHeightM.toFixed(1)} m`)
	if (parts.length === 1) parts.push(buoy.time ? "no met report" : "no recent report")
	return parts.join(" · ")
}

export class StormColumnNode extends GestureNode implements InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public readonly storm: StormProperties
	public readonly layout: StormColumnLayout
	public readonly windModel?: WindModel

	private readonly columnContext: EarthBrowserContext
	private readonly cameraAdapter: EntityCameraAdapter
	private readonly frame: NodeBase
	private readonly walls: StormColumnWallsNode
	private readonly lid: StormCloudLidNode
	private readonly windFloor: StormWindFloorNode
	private slab?: StormSstSlabNode
	private slabAnim?: animation
	private lightning?: StormColumnLightningNode
	private lightningAnim?: animation
	private readonly cloudSectionX: StormSectionNode
	private readonly cloudSectionZ: StormSectionNode
	private readonly windSectionX?: StormSectionNode
	private readonly windSectionZ?: StormSectionNode
	private readonly labels: NodeBase
	private buoyPins: BuoyPin[] = []
	private shearNodes: PolylineNode[] = []
	private probabilityNodes: PolylineNode[] = []
	private windProbability?: WindProbability
	private shearTip?: { x: number; z: number; y: number; text: string }
	private shearLabel?: TextNode
	private heightLabels: TextNode[] = []
	private pressureLabels: TextNode[] = []
	private infoLabel?: TextNode
	private font?: Font

	private readonly hitBox: cube
	private readonly inverseTransform: matrix
	private readonly boxHeight: number
	private far: FarWalls = { signX: 1, signZ: 1 }
	private farApplied = false
	private revealAnim?: animation
	private lidAnim?: animation
	private destroyed = false

	/** IR frames for the lid, oldest first; empty until the layer delivers them */
	private fields: StormCloudField[] = []
	private displayed?: StormCloudField
	private lastClockMs = Number.NaN

	/** Fired when focus leaves the column for something else (user clicked away) */
	public onDismissed?: () => void
	/** Fired when the frame on the lid changes (clock scrub or new frames) */
	public onFrameChanged?: (field: StormCloudField) => void

	constructor(context: EarthBrowserContext, storm: StormProperties, layout: StormColumnLayout, windModel: WindModel | undefined, observed: StormTrackPoint[], forecast: StormTrackPoint[]) {
		super(context)
		this.columnContext = context
		this.storm = storm
		this.layout = layout
		this.windModel = windModel
		this.id = `storm-column-${storm.id}`
		this.boxHeight = boxHeightKm(layout)

		this.transform = this.buildWorldTransform()
		this.inverseTransform = this.transform.invert()

		this.frame = new NodeBase()
		this.frame.transform = matrix.translate(0, -this.boxHeight / 2, 0)
		this.addNode(this.frame)

		this.walls = new StormColumnWallsNode(context, layout)
		this.frame.addNode(this.walls)

		// The floor always exists (it carries the SST tint and isotherm);
		// without a wind model it draws no wind
		this.windFloor = new StormWindFloorNode(context, layout, windModel ?? { vmaxKt: 0, rmwKm: 30, b: [1, 1, 1, 1], source: "climatology", rmwSource: "climatology" })
		this.frame.addNode(this.windFloor)
		if (windModel) {
			this.windSectionX = new StormSectionNode(context, cWindSectionColor)
			this.windSectionZ = new StormSectionNode(context, cWindSectionColor)
			this.frame.addNode(this.windSectionX)
			this.frame.addNode(this.windSectionZ)
			this.buildWindSections()
		}

		this.cloudSectionX = new StormSectionNode(context, cCloudSectionColor)
		this.cloudSectionZ = new StormSectionNode(context, cCloudSectionColor)
		this.frame.addNode(this.cloudSectionX)
		this.frame.addNode(this.cloudSectionZ)

		this.lid = new StormCloudLidNode(context, layout)
		this.lid.reveal = 0
		this.frame.addNode(this.lid)

		this.buildEdges()
		this.buildPlumbLine()
		this.buildTracks(observed, forecast)
		this.buildForecastColumns(forecast)

		this.labels = new NodeBase()
		this.frame.addNode(this.labels)
		void FontCache.load(cLabelFont).then(font => {
			if (this.destroyed) return
			this.font = font
			this.buildLabels()
			this.buildBuoyLabels()
			this.buildShearLabel()
		})

		const halfX = layout.lengthKm / 2
		const halfZ = layout.widthKm / 2
		this.hitBox = new cube()
		this.hitBox.update(-halfX, -this.boxHeight / 2, -halfZ)
		this.hitBox.update(halfX, this.boxHeight / 2, halfZ)

		const extent = Math.max(layout.lengthKm, layout.widthKm, this.boxHeight * 1.6)
		const focusDistance = clamp(2.1 * extent, 300, 6000)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, Math.max(800, extent * 6))

		this.revealAnim = new animation()
			.from(0).to(1).duration(0.9).easing(easing.quadraticOut)
			.onUpdate(v => {
				this.walls.opacity = v
				this.windFloor.opacity = v
				if (this.windSectionX) this.windSectionX.opacity = v
				if (this.windSectionZ) this.windSectionZ.opacity = v
				this.cloudSectionX.opacity = v
				this.cloudSectionZ.opacity = v
			})
		this.revealAnim.start()
	}

	// ========== Frames ==========

	/** IR frames arrive after their own fetch; the lid rises out of the sea-surface plane */
	public setFields(fields: StormCloudField[]): void {
		if (this.destroyed) return
		const first = this.fields.length === 0
		this.fields = [...fields].sort((a, b) => a.timeMs - b.timeMs)
		this.lastClockMs = Number.NaN
		if (first && this.fields.length > 0) {
			this.lidAnim = new animation()
				.from(0).to(1).duration(1.1).easing(easing.quadraticInOut)
				.onUpdate(v => { this.lid.reveal = v })
			this.lidAnim.start()
		}
	}

	public get displayedField(): StormCloudField | undefined {
		return this.displayed
	}

	/**
	 * Buoys inside the footprint as pins on the sea surface: a post, a bead
	 * and a label with the station's latest pressure, gust and wave height.
	 * In-situ truth standing in the modeled wind field
	 */
	public setBuoys(buoys: StormCutoutBuoy[]): void {
		if (this.destroyed) return
		for (const pin of this.buoyPins) {
			this.frame.removeNode(pin.post)
			pin.post.destroy()
			this.frame.removeNode(pin.bead)
			pin.bead.destroy()
			if (pin.label) {
				this.labels.removeNode(pin.label)
				pin.label.destroy()
			}
		}
		this.buoyPins = []
		const height = this.boxHeight * cBuoyPinFraction
		const beadKm = Math.max(3, 0.006 * this.layout.lengthKm)
		for (const buoy of buoys) {
			const xz = latLonToBlock(this.layout, buoy.lat, buoy.lon)
			if (!insideColumn(this.layout, xz.x, xz.z, -beadKm)) continue
			const post = new PolylineNode(this.columnContext, {
				positions: [new vec3(xz.x, 0.5, xz.z), new vec3(xz.x, height, xz.z)],
				width: 1.2, color: cBuoyColor, depthWrite: false
			})
			post.renderOrder = 3
			this.frame.addNode(post)
			const bead = new SimpleNode(this.columnContext, new Cube(this.columnContext), cBuoyColor)
			bead.transform = matrix.scale(beadKm, beadKm, beadKm).multiply(matrix.translate(xz.x, height, xz.z))
			this.frame.addNode(bead)
			this.buoyPins.push({ post, bead, x: xz.x, z: xz.z, height, text: buoyLabelText(buoy) })
		}
		this.buildBuoyLabels()
	}

	private buildBuoyLabels(): void {
		if (!this.font) return
		for (const pin of this.buoyPins) {
			if (pin.label) continue
			pin.label = new TextNode(this.columnContext, pin.text, this.font, this.labelSizeKm * 0.7)
			pin.label.color = cBuoyLabelColor
			this.labels.addNode(pin.label)
		}
		this.farApplied = false
	}

	/**
	 * Deep-layer shear as an arrow over the lid: shaft along the heading the
	 * 850–200 hPa shear vector points toward, length with its speed, so the
	 * side the convection is being pushed to reads directly off the block
	 */
	public setShear(kt: number, headingDeg: number): void {
		if (this.destroyed || kt <= 0) return
		for (const node of this.shearNodes) {
			this.frame.removeNode(node)
			node.destroy()
		}
		this.shearNodes = []
		const rel = headingDeg * trig.rad - this.layout.strike
		const dir = new vec3(Math.cos(rel), 0, Math.sin(rel))
		const side = new vec3(-dir.z, 0, dir.x)
		const length = clamp(kt / 40, 0.35, 1.1) * 0.22 * this.layout.lengthKm
		// Just above the box top so the eyewall tower never hides it
		const y = this.boxHeight * 1.05
		const tail = dir.multiply(-length / 2).add(new vec3(0, y, 0))
		const tip = dir.multiply(length / 2).add(new vec3(0, y, 0))
		const head = length * 0.14
		const barbA = tip.subtract(dir.multiply(head)).add(side.multiply(head * 0.55))
		const barbB = tip.subtract(dir.multiply(head)).subtract(side.multiply(head * 0.55))
		const line = (positions: vec3[]): void => {
			const node = new PolylineNode(this.columnContext, { positions, width: 2.2, color: cShearColor, depthWrite: false })
			node.renderOrder = 3
			this.frame.addNode(node)
			this.shearNodes.push(node)
		}
		line([tail, tip])
		line([barbA, tip, barbB])
		this.shearTip = { x: tip.x, z: tip.z, y, text: `shear ${Math.round(kt)} kt` }
		this.buildShearLabel()
	}

	private buildShearLabel(): void {
		if (!this.font || !this.shearTip || this.shearLabel) return
		this.shearLabel = new TextNode(this.columnContext, this.shearTip.text, this.font, this.labelSizeKm * 0.8)
		this.shearLabel.color = cShearColor
		this.labels.addNode(this.shearLabel)
		this.farApplied = false
	}

	/**
	 * GLM flashes arrive after their own fetch: sparks ride the lid at the
	 * cloud-top height of the displayed frame, fading in with the set
	 */
	public setLightning(flashes: StormColumnFlash[]): void {
		if (this.destroyed) return
		if (!this.lightning) {
			const node = new StormColumnLightningNode(this.columnContext, this.layout)
			node.opacity = 0
			node.setField(this.displayed)
			this.lightning = node
			this.frame.addNode(node)
			this.lightningAnim = new animation()
				.from(0).to(1).duration(0.8).easing(easing.quadraticOut)
				.onUpdate(v => { node.opacity = v })
			this.lightningAnim.start()
		}
		this.lightning.setFlashes(flashes)
	}

	/** Flashes kept inside the footprint and those drawn for the current clock (diagnostics) */
	public get lightningCounts(): { kept: number; visible: number } | undefined {
		return this.lightning ? { kept: this.lightning.count, visible: this.lightning.visibleCount } : undefined
	}

	/**
	 * NHC wind speed probability contours for one threshold, clipped to the
	 * footprint and laid on the sea-surface plane: where the winds may
	 * reach over the next 120 h, against the modeled field of right now.
	 * Colored with the same ramp as the globe swath (legend in the HUD)
	 */
	public setWindProbability(probability: WindProbability, kt: WindProbabilityThreshold): void {
		if (this.destroyed) return
		this.windProbability = probability
		this.buildProbabilityContours(kt)
	}

	public setWindProbabilityThreshold(kt: WindProbabilityThreshold): void {
		if (this.destroyed || !this.windProbability) return
		this.buildProbabilityContours(kt)
	}

	private buildProbabilityContours(kt: WindProbabilityThreshold): void {
		for (const node of this.probabilityNodes) {
			this.frame.removeNode(node)
			node.destroy()
		}
		this.probabilityNodes = []
		if (!this.windProbability) return
		const margin = -0.004 * this.layout.lengthKm
		for (const band of polygonsFor(this.windProbability, kt)) {
			const c = probabilityColor(band.pct)
			const color = rgba(c.r, c.g, c.b, 0.85)
			// Runs of consecutive in-footprint vertices become separate lines
			let run: vec3[] = []
			const flush = (): void => {
				if (run.length >= 2) {
					const node = new PolylineNode(this.columnContext, { positions: run, width: 1.6, color, depthWrite: false })
					node.renderOrder = 3
					this.frame.addNode(node)
					this.probabilityNodes.push(node)
				}
				run = []
			}
			for (const [lon, lat] of band.ring) {
				const xz = latLonToBlock(this.layout, lat, lon)
				if (insideColumn(this.layout, xz.x, xz.z, margin)) run.push(new vec3(xz.x, 0.8, xz.z))
				else flush()
			}
			flush()
		}
	}

	/** Radius of the outer closed isobar (best track): a faint dashed ring on the floor marking the storm's pressure footprint */
	public setOuterClosedIsobar(rociKm: number): void {
		if (this.destroyed) return
		this.windFloor.setOuterClosedIsobar(rociKm)
	}

	/** Sea surface temperature arrives after its own fetch: slab faces and the floor tint fade in */
	public setSst(field: StormSstField): void {
		if (this.destroyed || this.slab) return
		this.windFloor.setSst(field, this.columnContext)
		const slab = new StormSstSlabNode(this.columnContext, this.layout, field)
		this.slab = slab
		this.frame.addNode(slab)
		this.hitBox.update(0, -this.boxHeight / 2 - slab.slabKm, 0)
		this.slabAnim = new animation()
			.from(0).to(1).duration(0.8).easing(easing.quadraticOut)
			.onUpdate(v => { slab.opacity = v })
		this.slabAnim.start()
	}

	public get frameCount(): number {
		return this.fields.length
	}

	/** Pick the frame pair bracketing the clock and crossfade the lid between them */
	private updateFrames(clockMs: number): void {
		const fields = this.fields
		if (fields.length === 0) return
		let a = fields[0]
		let b = fields[0]
		let blend = 0
		if (clockMs >= fields[fields.length - 1].timeMs) {
			a = b = fields[fields.length - 1]
		} else if (clockMs > fields[0].timeMs) {
			let lo = 0
			let hi = fields.length - 1
			while (hi - lo > 1) {
				const mid = (lo + hi) >> 1
				if (fields[mid].timeMs <= clockMs) lo = mid
				else hi = mid
			}
			a = fields[lo]
			b = fields[hi]
			const span = b.timeMs - a.timeMs
			blend = span > 0 ? (clockMs - a.timeMs) / span : 0
		}
		this.lid.setFrames(a, b, blend)
		const nearest = blend < 0.5 ? a : b
		if (nearest !== this.displayed) {
			this.displayed = nearest
			this.buildCloudSections(nearest)
			this.lightning?.setField(nearest)
			this.onFrameChanged?.(nearest)
		}
	}

	// ========== Frame ==========

	/**
	 * World placement: tangent frame at the storm center rotated to the
	 * motion direction, lifted so the sea-surface plane clears the globe by
	 * hoverKm and the node origin sits at mid height
	 */
	private buildWorldTransform(): matrix {
		const layout = this.layout
		const center = new geographic(layout.centerLon * trig.rad, layout.centerLat * trig.rad, 0).cartesian
		const up = normal(center)
		const north = normal(vec3.unitY.subtract(up.multiply(dot(vec3.unitY, up))))
		const east = cross(north, up)
		const s = Math.sin(layout.strike)
		const c = Math.cos(layout.strike)
		const xAxis = east.multiply(s).add(north.multiply(c))
		const zAxis = east.multiply(c).subtract(north.multiply(s))
		const origin = center.add(up.multiply(layout.hoverKm + this.boxHeight / 2))
		return new matrix([
			xAxis.x, xAxis.y, xAxis.z, 0,
			up.x, up.y, up.z, 0,
			zAxis.x, zAxis.y, zAxis.z, 0,
			origin.x, origin.y, origin.z, 1
		])
	}

	// ========== Static dressing ==========

	private buildEdges(): void {
		const hx = this.layout.lengthKm / 2
		const hz = this.layout.widthKm / 2
		const top = this.boxHeight
		const line = (positions: vec3[], loop: boolean): void => {
			const node = new PolylineNode(this.columnContext, { positions, loop, width: 1.2, color: cEdgeColor, depthWrite: false })
			node.renderOrder = 3
			this.frame.addNode(node)
		}
		line([new vec3(-hx, 0, -hz), new vec3(hx, 0, -hz), new vec3(hx, 0, hz), new vec3(-hx, 0, hz)], true)
		line([new vec3(-hx, top, -hz), new vec3(hx, top, -hz), new vec3(hx, top, hz), new vec3(-hx, top, hz)], true)
		for (const [x, z] of [[-hx, -hz], [hx, -hz], [hx, hz], [-hx, hz]]) {
			line([new vec3(x, 0, z), new vec3(x, top, z)], false)
		}
	}

	/**
	 * Plumb line through the storm center: from the sea surface up through
	 * the eye, and from the sea-surface plane down to the real sea below,
	 * tying the floating block back to the place it was lifted from
	 */
	private buildPlumbLine(): void {
		const inner = new PolylineNode(this.columnContext, {
			positions: [new vec3(0, 0.01, 0), new vec3(0, this.boxHeight * 0.96, 0)],
			width: 1.5, color: cPlumbColor, depthWrite: false
		})
		inner.renderOrder = 3
		this.frame.addNode(inner)

		const groundElevKm = this.columnContext.displayElevationKm(new geographic(this.layout.centerLon * trig.rad, this.layout.centerLat * trig.rad, 0))
		const groundY = -this.layout.hoverKm + groundElevKm
		const outer = new PolylineNode(this.columnContext, {
			positions: [new vec3(0, 0, 0), new vec3(0, groundY, 0)],
			width: 1.2, color: cPlumbGroundColor, depthWrite: false
		})
		outer.renderOrder = 3
		this.frame.addNode(outer)
	}

	/** Observed and forecast track on the sea-surface plane, clipped to the footprint */
	private buildTracks(observed: StormTrackPoint[], forecast: StormTrackPoint[]): void {
		const toPositions = (points: StormTrackPoint[]): vec3[] => {
			const out: vec3[] = []
			for (const p of points) {
				const xz = latLonToBlock(this.layout, p.lat, p.lon)
				if (!insideColumn(this.layout, xz.x, xz.z)) continue
				out.push(new vec3(xz.x, 0.6, xz.z))
			}
			return out
		}
		const observedPositions = toPositions(observed)
		if (observedPositions.length >= 2) {
			const node = new PolylineNode(this.columnContext, { positions: observedPositions, width: 1.5, color: cTrackObservedColor, depthWrite: false })
			node.renderOrder = 3
			this.frame.addNode(node)
		}
		const lastObserved = observed.length > 0 ? observed[observed.length - 1] : undefined
		const forecastPositions = toPositions(lastObserved ? [lastObserved, ...forecast.filter(p => p.t > lastObserved.t)] : forecast)
		if (forecastPositions.length >= 2) {
			const node = new PolylineNode(this.columnContext, { positions: forecastPositions, width: 1.5, color: cTrackForecastColor, depthWrite: false })
			node.renderOrder = 3
			this.frame.addNode(node)
		}
	}

	/**
	 * Forecast intensity as translucent columns at each forecast point inside
	 * the footprint: height follows the forecast sustained wind, color the
	 * Saffir–Simpson category, so the intensity trend is spatial
	 */
	private buildForecastColumns(forecast: StormTrackPoint[]): void {
		const width = Math.max(6, 0.012 * this.layout.lengthKm)
		for (const p of forecast) {
			if ((p.tau ?? 0) <= 0 || p.wind_kt === null) continue
			const xz = latLonToBlock(this.layout, p.lat, p.lon)
			if (!insideColumn(this.layout, xz.x, xz.z, -width)) continue
			const h = clamp(p.wind_kt / cColumnFullKt, 0.05, 1) * this.boxHeight
			const base = DesignColors.stormCategoryColor(p.category, p.wind_kt)
			const node = new SimpleNode(this.columnContext, new Cube(this.columnContext), rgba(base.r, base.g, base.b, 0.32))
			node.transform = matrix.scale(width, h, width).multiply(matrix.translate(xz.x, h / 2, xz.z))
			node.renderOrder = 1
			this.frame.addNode(node)
		}
	}

	// ========== Sections ==========

	/** Modeled wind along both axes through the center, as a wall inset */
	private buildWindSections(): void {
		if (!this.windModel || !this.windSectionX || !this.windSectionZ) return
		const model = this.windModel
		const insetTop = this.boxHeight * cWindInsetFraction
		const toHeight = (kt: number): number => clamp(kt / cWindInsetTopKt, 0, 1.2) * insetTop
		const hx = this.layout.lengthKm / 2
		const hz = this.layout.widthKm / 2
		const alongX: number[] = []
		const windX: number[] = []
		for (let i = 0; i <= cSectionSamples; i++) {
			const x = -hx + (2 * hx * i) / cSectionSamples
			alongX.push(x)
			windX.push(toHeight(hollandWindKt(model, Math.abs(x), blockBearingRad(this.layout, x, 0))))
		}
		const alongZ: number[] = []
		const windZ: number[] = []
		for (let i = 0; i <= cSectionSamples; i++) {
			const z = -hz + (2 * hz * i) / cSectionSamples
			alongZ.push(z)
			windZ.push(toHeight(hollandWindKt(model, Math.abs(z), blockBearingRad(this.layout, 0, z))))
		}
		this.windSectionX.setProfile("x", alongX, windX)
		this.windSectionZ.setProfile("z", alongZ, windZ)
	}

	/**
	 * Cloud-top profile through the storm's position at frame time, along
	 * and across the motion, from the displayed frame
	 */
	private buildCloudSections(field: StormCloudField): void {
		const center = latLonToBlock(this.layout, field.centerLat, field.centerLon)
		const hx = this.layout.lengthKm / 2
		const hz = this.layout.widthKm / 2
		const scale = this.layout.heightScale
		const alongX: number[] = []
		const topsX: number[] = []
		for (let i = 0; i <= cSectionSamples; i++) {
			const x = -hx + (2 * hx * i) / cSectionSamples
			const ll = blockToLatLon(this.layout, x, center.z)
			alongX.push(x)
			topsX.push(cloudTopKm(field.sample(ll.lat, ll.lon)) * scale)
		}
		const alongZ: number[] = []
		const topsZ: number[] = []
		for (let i = 0; i <= cSectionSamples; i++) {
			const z = -hz + (2 * hz * i) / cSectionSamples
			const ll = blockToLatLon(this.layout, center.x, z)
			alongZ.push(z)
			topsZ.push(cloudTopKm(field.sample(ll.lat, ll.lon)) * scale)
		}
		this.cloudSectionX.setProfile("x", alongX, topsX)
		this.cloudSectionZ.setProfile("z", alongZ, topsZ)
	}

	/** Lay the section strips onto the far walls, a hair toward the camera */
	private layoutSections(): void {
		const hx = this.layout.lengthKm / 2
		const hz = this.layout.widthKm / 2
		const inset = 0.002 * this.layout.lengthKm
		const zWall = matrix.translate(0, 0.02, -this.far.signZ * hz + this.far.signZ * inset)
		const xWall = matrix.translate(-this.far.signX * hx + this.far.signX * inset, 0.02, 0)
		this.cloudSectionX.transform = zWall
		this.cloudSectionZ.transform = xWall
		if (this.windSectionX) this.windSectionX.transform = matrix.translate(0, 0.04, -this.far.signZ * hz + this.far.signZ * inset * 2)
		if (this.windSectionZ) this.windSectionZ.transform = matrix.translate(-this.far.signX * hx + this.far.signX * inset * 2, 0.04, 0)
	}

	// ========== Labels ==========

	private get labelSizeKm(): number {
		return clamp(0.026 * this.layout.lengthKm, 4, 40)
	}

	private buildLabels(): void {
		if (!this.font) return
		const layout = this.layout
		const ticks = Math.floor(layout.heightKm / layout.tickKm)
		for (let k = 0; k <= ticks; k++) {
			const km = k * layout.tickKm
			const node = new TextNode(this.columnContext, `${km} km`, this.font, this.labelSizeKm)
			node.color = k === 0 ? cLabelColor : cLabelDimColor
			this.labels.addNode(node)
			this.heightLabels.push(node)
		}
		for (const level of cPressureLevels) {
			const node = new TextNode(this.columnContext, level.label, this.font, this.labelSizeKm * 0.85)
			node.color = cPressureLabelColor
			this.labels.addNode(node)
			this.pressureLabels.push(node)
		}
		const parts = [`Height ×${layout.heightScale}`, `Grid ${this.walls.gridKm} km`, "IR heights ±1–2 km"]
		if (this.windModel) {
			parts.push(this.windModel.source === "fit" ? "wind modeled from advisory radii" : "wind modeled, climatological profile")
			parts.push(this.windModel.rmwSource === "atcf" ? "RMW from NHC best track" : "RMW from climatology")
		}
		this.infoLabel = new TextNode(this.columnContext, parts.join("   ·   "), this.font, this.labelSizeKm * 0.8)
		this.infoLabel.color = cLabelDimColor
		this.labels.addNode(this.infoLabel)
		this.farApplied = false
	}

	/**
	 * Height labels on the far z wall next to the far x corner; pressure
	 * labels on the far x wall next to the far z corner; the info label
	 * along the top of the far z wall
	 */
	private layoutLabels(): void {
		if (!this.font) return
		const layout = this.layout
		const size = this.labelSizeKm
		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const sx = this.far.signX
		const sz = this.far.signZ
		const wallZ = -sz * hz + sz * size * 0.05
		const wallX = -sx * hx + sx * size * 0.05

		this.heightLabels.forEach((node, k) => {
			const width = node.text.length * size * 0.56
			const x = -sx * hx + sx * (width / 2 + size * 0.5)
			const y = k * layout.tickKm * layout.heightScale + size * 0.55
			node.transform = zWallText(sz, new vec3(x, y, wallZ))
		})

		this.pressureLabels.forEach((node, i) => {
			const level = cPressureLevels[i]
			const width = node.text.length * size * 0.85 * 0.56
			// Text runs along z on the x wall, inward from the far z corner
			const z = -sz * hz + sz * (width / 2 + size * 0.5)
			const y = level.km * layout.heightScale + size * 0.5
			node.transform = xWallText(sx, new vec3(wallX, y, z))
		})

		if (this.infoLabel) {
			const y = this.boxHeight + size * 1.1
			this.infoLabel.transform = zWallText(sz, new vec3(0, y, wallZ))
		}

		if (this.shearLabel && this.shearTip) {
			const labelSize = size * 0.8
			const width = this.shearTip.text.length * labelSize * 0.56
			this.shearLabel.transform = zWallText(sz, new vec3(this.shearTip.x + sx * (width / 2 + labelSize), this.shearTip.y + labelSize * 0.9, this.shearTip.z))
		}

		// Buoy labels sit beside their bead, facing the camera's z side
		for (const pin of this.buoyPins) {
			if (!pin.label) continue
			const labelSize = size * 0.7
			const width = pin.text.length * labelSize * 0.56
			pin.label.transform = zWallText(sz, new vec3(pin.x + sx * (width / 2 + labelSize), pin.height + labelSize * 0.2, pin.z + sz * labelSize * 0.3))
		}
	}

	// ========== Per frame ==========

	override render(state: RenderState): RenderActionSet[] {
		const clockMs = this.columnContext.clock.clockTime.date.getTime()
		if (clockMs !== this.lastClockMs) {
			this.lastClockMs = clockMs
			this.updateFrames(clockMs)
		}
		this.lightning?.update(clockMs)
		this.windFloor.tick()

		const camLocal = this.inverseTransform.multVec3(state.camera.position, 1)
		const signX = camLocal.x >= 0 ? 1 : -1
		const signZ = camLocal.z >= 0 ? 1 : -1
		if (!this.farApplied || signX !== this.far.signX || signZ !== this.far.signZ) {
			this.far = { signX, signZ }
			this.farApplied = true
			this.walls.setFarWalls(this.far)
			this.layoutSections()
			this.layoutLabels()
		}

		return super.render(state)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitBox.intersect(state.ray)
		if (dist > 0) {
			return { node: this, hitPoint: state.ray.pointAt(dist), distance: dist }
		}
		return undefined
	}

	// ========== Focus lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)
	}

	/**
	 * An info-panel provider with no panel: the HUD replaces the anchored
	 * card, and returning undefined makes the focus plumbing hide the
	 * callout without closing the storm's info window or clearing the URL
	 * target (which would deselect the storm and dismiss this block)
	 */
	public getInfoPanelData(): InfoPanelData | undefined {
		return undefined
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.onDismissed?.()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	/** Fly to an oblique three-quarter view from whichever quadrant the camera is already in */
	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.defaultCameraState(camera),
			params: { duration: 0.8, easing: "easeInOut" }
		}
	}

	private defaultCameraState(camera: Camera): CameraState {
		const camLocal = this.inverseTransform.multVec3(camera.position, 1)
		const sx = camLocal.x >= 0 ? 1 : -1
		const sz = camLocal.z >= 0 ? 1 : -1
		const sinT = Math.sin(cDefaultTiltFromNadir)
		const cosT = Math.cos(cDefaultTiltFromNadir)
		const dirLocal = new vec3(
			sinT * Math.sin(cDefaultAzimuth) * sx,
			cosT,
			sinT * Math.cos(cDefaultAzimuth) * sz
		)
		const distance = this.cameraAdapter.getTargetCameraState(camera).position.subtract(this.transform.position).length
		const dirWorld = normal(this.transform.multVec3(dirLocal, 0))
		const origin = this.transform.position
		const position = origin.add(dirWorld.multiply(distance))
		const forward = normal(origin.subtract(position))
		const upHint = normal(origin)
		const up = normal(upHint.subtract(forward.multiply(dot(upHint, forward))))
		return new CameraState(position, forward, up)
	}

	/**
	 * Camera presets for the block, as compass bearing + tilt in the entity
	 * camera's tangent frame: the oblique default, edge-on to either section
	 * wall (the wall on the camera's far side stays the drawn one), and
	 * straight down onto the wind field
	 */
	public viewPreset(preset: StormColumnViewPreset): void {
		const strike = this.layout.strike
		const sx = this.far.signX
		const sz = this.far.signZ
		let viewBearing: number
		let tilt: number
		switch (preset) {
			case "along":
				// Look across the block at the z wall: view direction ∓z
				viewBearing = strike - sz * trig.half_pi
				tilt = cSectionTiltFromNadir
				break
			case "across":
				// Look along the motion axis at the x wall
				viewBearing = sx > 0 ? strike + trig.pi : strike
				tilt = cSectionTiltFromNadir
				break
			case "overhead":
				viewBearing = strike
				tilt = cOverheadTiltFromNadir
				break
			default: {
				const dirX = sx * Math.sin(cDefaultAzimuth)
				const dirZ = sz * Math.cos(cDefaultAzimuth)
				viewBearing = strike + Math.atan2(dirZ, dirX) + trig.pi
				tilt = cDefaultTiltFromNadir
			}
		}
		this.cameraAdapter.animatedTiltAndBearing(tilt, viewBearing)
	}

	public handleCommand(command: string, data?: unknown): boolean {
		if (command === "column/view") {
			const preset = (data as { preset?: StormColumnViewPreset } | undefined)?.preset ?? "oblique"
			this.viewPreset(preset)
			return true
		}
		return this.cameraAdapter.handleCommand(command, data)
	}

	override destroy(): void {
		this.destroyed = true
		this.revealAnim?.stop()
		this.lidAnim?.stop()
		this.slabAnim?.stop()
		this.lightningAnim?.stop()
		this.onDismissed = undefined
		this.onFrameChanged = undefined
		super.destroy()
	}
}

/**
 * Transform placing text (centered, in its own xy plane) on a z wall at
 * `at`, reading left-to-right from the camera side. TextGeometry reads from
 * its -z side with +y down, so a proper rotation keeps the glyphs unmirrored
 */
function zWallText(signZ: number, at: vec3): matrix {
	const right = new vec3(signZ, 0, 0)
	const up = new vec3(0, -1, 0)
	const face = new vec3(0, 0, -signZ)
	return new matrix([
		right.x, right.y, right.z, 0,
		up.x, up.y, up.z, 0,
		face.x, face.y, face.z, 0,
		at.x, at.y, at.z, 1
	])
}

/** Same for an x wall: text runs along z, facing the camera side signX */
function xWallText(signX: number, at: vec3): matrix {
	const right = new vec3(0, 0, -signX)
	const up = new vec3(0, -1, 0)
	const face = new vec3(-signX, 0, 0)
	return new matrix([
		right.x, right.y, right.z, 0,
		up.x, up.y, up.z, 0,
		face.x, face.y, face.z, 0,
		at.x, at.y, at.z, 1
	])
}
